import _ from 'lodash'
import { AxiosResponse } from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { PaginationConfig } from 'antd/lib/pagination'

import { apiCall } from '../../../utils/api'
import { isUserLoggedIn } from '../../user/selectors'
import { withLoading } from '../../loading'
import { TableActions, ColumnSorting, sortParam } from '../../table'
import { Dictionary } from '../../../utils/types'
import { selectSorting, selectPagination } from '../../table/selectors'

import { DialogActions } from '../../../redux/dialog'

import { ProfileManagementActions } from './actions'
import { InvitedUsersResponse } from './types'
import { getProfileManagementFilters, getProfileManagementSearchFilter } from './selectors'

type LoadInvitedUsersType = ReturnType<typeof ProfileManagementActions.loadInvitedUsers>

const preprocessFilters = (filters: Dictionary<any>) =>
  _.mapValues(filters, filter => (_.has(filter, 'value') ? _.get(filter, 'value') : filter))

export function* loadInvitedUsers(action: LoadInvitedUsersType): any {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return

  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'profileManagement' })
  const filter: string = yield select(getProfileManagementSearchFilter)
  const filters: Dictionary<any> = yield select(getProfileManagementFilters)
  const preprocessedFilters = preprocessFilters(filters)

  const { data: profileManagementData }: AxiosResponse<InvitedUsersResponse> = yield call(apiCall, {
    url: '/api/invitedUsers',
    method: 'post',
    params: {
      page: action.payload.page,
      pageSize: action.payload.pageSize,
      sort: sortParam(action.payload.sorting || sorting),
      search: filter || undefined
    },
    data: {
      ...preprocessedFilters
    }
  })

  yield put(
    ProfileManagementActions.setInvitedUsers({
      page: profileManagementData.currentPage,
      totalPages: profileManagementData.pagesCount,
      data: profileManagementData.records
    })
  )

  if (profileManagementData.currentPage === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'profileManagement',
        pagination: {
          current: profileManagementData.currentPage,
          pageSize: action.payload.pageSize,
          total: profileManagementData.pagesCount * action.payload.pageSize
        }
      })
    )
  }
}

export function* reloadInvitedUsers(): any {
  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'profileManagement' })
  const page = pagination.current || 1
  const pageSize = pagination.pageSize || pagination.defaultPageSize || 10
  yield put(ProfileManagementActions.loadInvitedUsers({ page, pageSize }))
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'profileManagement') return

  const { pagination } = payload

  yield put(
    ProfileManagementActions.loadInvitedUsers({
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* loadOnSortChange({ payload }: ReturnType<typeof TableActions.setSorting>) {
  if (payload.tableKey !== 'profileManagement') return

  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'profileManagement' })

  yield put(
    ProfileManagementActions.loadInvitedUsers({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || pagination.defaultPageSize || 10
    })
  )
}

export function* loadOnFilterChange() {
  const pagination: PaginationConfig = yield select(selectPagination, { tableKey: 'profileManagement' })

  yield put(
    ProfileManagementActions.loadInvitedUsers({
      page: 1,
      pageSize: pagination.pageSize || pagination.defaultPageSize || 10
    })
  )
}

export function* openDialogToInvitationLinkGeneration() {
  yield put(
    DialogActions.showDialogWithData({
      dialog: 'ProfileManagementInvitationLinkGeneration',
      data: {}
    })
  )
}

/* istanbul ignore next */
export function* profileManagementSagas() {
  yield all([
    takeLatest(getType(ProfileManagementActions.loadInvitedUsers), withLoading('profileManagement', loadInvitedUsers)),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(TableActions.setSorting), loadOnSortChange),
    takeLatest(getType(ProfileManagementActions.setFilters), loadOnFilterChange),
    takeLatest(getType(ProfileManagementActions.clearFilter), loadOnFilterChange),
    takeLatest(getType(ProfileManagementActions.reloadInvitedUsers), reloadInvitedUsers),
    takeLatest(
      getType(ProfileManagementActions.openDialogToInvitationLinkGeneration),
      openDialogToInvitationLinkGeneration
    )
  ])
}
